import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import type { ChatSessionMode } from '@karehero/models';
import {
  SandboxPage,
  type SandboxConversation,
} from '@karehero/llama/pwa/pages/SandboxPage/SandboxPage';

import { DYNAMIC_ROUTES } from 'router/routes';
import { useDispatch, useSelector } from 'store';
import {
  useCreateChatSessionMutation,
  useListChatSessionsQuery,
  useListConversationConfigsQuery,
} from 'store/api/chatSession';
import { getCurrentCareCircle } from 'store/slices/careCircleSlice';
import { newToast } from 'store/slices/uiSlice';
import { useGoBack } from 'hooks/useGoBack';

/** The search param naming the conversation shown, so coming back from a session returns to it. */
const CONVERSATION_PARAM = 'conversation';

/** Sandbox wires the conversations for trying the chat engine out, and the sessions started of each. */
export const Sandbox = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const goBack = useGoBack();
  const careCircleId = useSelector(getCurrentCareCircle)?.id;
  const [createChatSession] = useCreateChatSessionMutation();
  const [startingMode, setStartingMode] = useState<ChatSessionMode>();
  const [searchParams, setSearchParams] = useSearchParams();
  // Checked against the conversations listed by the page, which shows the first otherwise.
  const selectedMode = (searchParams.get(CONVERSATION_PARAM) ?? undefined) as
    ChatSessionMode | undefined;

  const configsQuery = useListConversationConfigsQuery();
  // Every mode's sessions, sorted into the sandbox conversations below.
  const sessionsQuery = useListChatSessionsQuery(
    { careCircleId: careCircleId ?? '' },
    { skip: !careCircleId, refetchOnMountOrArgChange: true },
  );

  const conversations = useMemo<SandboxConversation[]>(
    () =>
      (configsQuery.data ?? [])
        .filter((config) => config.isSandbox)
        .map((config) => ({
          config,
          // Most recently updated first, as listed.
          sessions: (sessionsQuery.data ?? [])
            .filter(({ session }) => session.mode === config.mode)
            .map(({ session, messages }) => ({
              id: session.id,
              startedAt: session.createdAt,
              messageCount: messages.length,
            })),
        })),
    [configsQuery.data, sessionsQuery.data],
  );

  const startConversation = async (mode: ChatSessionMode) => {
    if (!careCircleId) return;
    setStartingMode(mode);
    try {
      const session = await createChatSession({
        careCircleId,
        body: { mode },
      }).unwrap();
      navigate(DYNAMIC_ROUTES.SANDBOX_CHAT(mode, session.id));
    } catch (err) {
      Sentry.captureException(err);
      dispatch(
        newToast({
          title: 'Something went wrong',
          description:
            'We could not start this conversation. Please try again.',
          variant: 'error',
        }),
      );
    } finally {
      setStartingMode(undefined);
    }
  };

  return (
    <SandboxPage
      conversations={conversations}
      selectedMode={selectedMode}
      startingMode={startingMode}
      isLoading={configsQuery.isLoading || sessionsQuery.isLoading}
      isError={configsQuery.isError || sessionsQuery.isError}
      onSelectConversation={(mode) =>
        setSearchParams({ [CONVERSATION_PARAM]: mode }, { replace: true })
      }
      onStartConversation={startConversation}
      onOpenSession={(mode, sessionId) =>
        navigate(DYNAMIC_ROUTES.SANDBOX_CHAT(mode, sessionId))
      }
      onBack={goBack}
    />
  );
};

Sandbox.displayName = 'Sandbox';
export default Sandbox;
